/**
 * Validates a redirect target is a same-origin relative path.
 *
 * An unvalidated `?redirect=` query param is an open-redirect phishing vector:
 * attackers send users to `/signup?redirect=https://evil.com`, the user trusts
 * the sharpr.bet domain, signs up, then gets bounced to the attacker site.
 *
 * A value is "safe" only if it:
 *   - is a non-empty string
 *   - contains no control chars (tab/LF/CR get stripped by the URL parser, so
 *     a value like "/<TAB>/evil.com" would collapse to "//evil.com" and escape
 *     the leading-`/` check below)
 *   - starts with `/`
 *   - does NOT start with `//` (protocol-relative URL — `//evil.com/path`)
 *   - does NOT start with `/\` (backslash-prefixed — some browsers treat as protocol-relative)
 *   - does NOT contain `\` anywhere (Windows-path smuggling)
 *
 * Anything else falls back to the provided default (or `/pricing`).
 */

/** True if the string contains any C0 control char (0x00–0x1F) or DEL (0x7F). */
function hasControlChars(value: string): boolean {
  for (let i = 0; i < value.length; i++) {
    const code = value.charCodeAt(i);
    if (code <= 0x1f || code === 0x7f) return true;
  }
  return false;
}

export function safeRedirect(value: string | null | undefined, fallback = '/pricing'): string {
  if (!value || typeof value !== 'string') return fallback;
  if (hasControlChars(value)) return fallback;
  if (!value.startsWith('/')) return fallback;
  if (value.startsWith('//') || value.startsWith('/\\')) return fallback;
  if (value.includes('\\')) return fallback;
  return value;
}
